/**
 * Anonymous, cookie-free visit statistics for the owner's dashboard (/stats, served by worker/index.ts).
 * Each tab keeps one running record (pages seen, seconds visible, games opened and seconds played) under a
 * random key that lives only in sessionStorage, and sends it to /api/hit now and then. Nothing identifies a
 * person: the only thing kept between visits is the date of the last visit, to count unique visitors.
 */
import { readJson, writeJson } from './storage';

const SEEN_KEY = 'sf:seen';
/** Set by visiting /?nostats=1 (linked from the owner's dashboard), so the owner's own visits are left out. */
const OPT_OUT_KEY = 'sf:no-stats';
const HEARTBEAT_MS = 60_000;

interface Visit {
  sid: string;
  newToday: boolean;
  /** On the first visit of the day: days since the previous visit day, or -1 for a first-ever visit. */
  gap: number | null;
  ref: string;
  landing: string;
  pages: number;
  secs: number;
  games: Record<string, [number, number]>;
}

let visit: Visit | null = null;
let currentGame: string | null = null;
let dirty = false;

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Whole days between two of today()'s date strings. */
function daysBetween(from: string, to: string): number {
  const ms = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return Date.UTC(y!, m! - 1, d!);
  };
  return Math.round((ms(to) - ms(from)) / 86400e3);
}

function enabled(): boolean {
  if (typeof window === 'undefined' || navigator.webdriver) return false;
  if (!/^(www\.)?playsuperfun\.com$/.test(location.hostname)) return false;
  const flag = new URLSearchParams(location.search).get('nostats');
  if (flag === '1' || flag === '0') writeJson(OPT_OUT_KEY, flag === '1');
  return !readJson<boolean>(OPT_OUT_KEY, false);
}

function send(): void {
  if (!visit || !dirty) return;
  dirty = false;
  const body = JSON.stringify(visit);
  try {
    if (navigator.sendBeacon?.('/api/hit', new Blob([body], { type: 'application/json' }))) return;
  } catch {
    /* fall through to fetch */
  }
  fetch('/api/hit', { method: 'POST', body, keepalive: true, headers: { 'content-type': 'application/json' } }).catch(() => {});
}

function start(path: string): void {
  let sid = '';
  try {
    sid = sessionStorage.getItem('sf:sid') ?? '';
    if (!sid) {
      sid = Array.from(crypto.getRandomValues(new Uint8Array(10)), (b) => (b % 36).toString(36)).join('');
      sessionStorage.setItem('sf:sid', sid);
    }
  } catch {
    sid = Array.from(crypto.getRandomValues(new Uint8Array(10)), (b) => (b % 36).toString(36)).join('');
  }
  const last = readJson<string>(SEEN_KEY, '');
  const newToday = last !== today();
  let gap: number | null = null;
  if (newToday) {
    const g = /^\d{4}-\d{1,2}-\d{1,2}$/.test(last) ? daysBetween(last, today()) : -1;
    gap = g > 0 ? g : -1;
    writeJson(SEEN_KEY, today());
  }
  let ref = '';
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : '';
    if (host && host !== location.hostname) ref = host;
  } catch {
    /* unreadable referrer */
  }
  visit = { sid, newToday, gap, ref, landing: path, pages: 0, secs: 0, games: {} };

  // Count only time the tab is actually on screen.
  window.setInterval(() => {
    if (!visit || document.visibilityState !== 'visible') return;
    visit.secs += 1;
    if (currentGame) visit.games[currentGame]![1] += 1;
    dirty = true;
  }, 1000);
  window.setInterval(send, HEARTBEAT_MS);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && send());
  window.addEventListener('pagehide', send);
}

/** Call on every route change. */
export function trackPage(path: string): void {
  if (!enabled()) return;
  if (!visit) start(path);
  const v = visit!;
  v.pages += 1;
  const m = /^\/play\/([a-z0-9-]+)/.exec(path);
  currentGame = m ? m[1]! : null;
  if (currentGame) {
    const g = (v.games[currentGame] ??= [0, 0]);
    g[0] += 1;
  }
  dirty = true;
  // Send the first page right away so short visits still count.
  if (v.pages === 1) send();
}

